import { useCallback, useEffect, useState, useRef, type SyntheticEvent, type ReactNode } from 'react';

import { Box, InputBase, Menu, MenuItem, type PopoverOrigin } from '@mui/material';

import { useDebounce } from 'use-debounce';

import useInfiniteScroll from 'react-infinite-scroll-hook';

import handleError from 'utils-ts/handle-error';
import callAzureFunction from 'utils-ts/call-azure-function';
import { type AxiosRequestConfig } from 'axios';

interface PaginatedMenuProps<T> {
  anchorEl: HTMLElement | null;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  requestConfig: AxiosRequestConfig;
  getOptionLabel: (option: T) => string;
  onClick: (event: SyntheticEvent<HTMLElement> | undefined, option: T) => void;
  onClose: () => void;
  renderMenuItemContent?: (option: T) => ReactNode;
}

const PaginatedMenu = <T,>({
  anchorEl,
  anchorOrigin,
  transformOrigin,
  requestConfig,
  getOptionLabel,
  onClick,
  onClose,
  renderMenuItemContent
}: PaginatedMenuProps<T>) => {
  const open = Boolean(anchorEl);

  const [options, setOptions] = useState<T[]>([]);
  const [page, setPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce<string>(search, 800);

  // Keep track of whether an initial load has happened for the current menu open state
  const hasInitialLoad = useRef(false);
  // Use a ref to store the current search term for infinite scroll
  const currentSearchTerm = useRef<string>('');

  const handleClose = () => {
    onClose();
    // Reset state when the menu closes
    setOptions([]);
    setPage(0);
    setHasNextPage(true);
    setSearch('');
    setError(null);
    hasInitialLoad.current = false; // Reset initial load flag
  };

  const handleClick = (event: SyntheticEvent<HTMLElement> | undefined, option: T) => {
    onClick(event, option);
  };

  const loadOptions = useCallback(
    async (pageToLoad: number, searchTerm: string, isNewSearch = false) => {
      if (loading) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const params = {
          page: pageToLoad,
          pageSize: 5,
          search: searchTerm
        };

        const { data } = await callAzureFunction<{ rows: T[]; hasNextPage: boolean }>({
          ...(requestConfig || {}),
          params: {
            ...(requestConfig.params || {}),
            ...params
          }
        });

        setOptions((prev) => {
          if (isNewSearch) {
            // If it's a new search (or initial load), replace options
            return data.rows;
          }
          return [...prev, ...data.rows]; // Otherwise, append
        });
        setHasNextPage(data.hasNextPage);
        setPage(pageToLoad + 1); // Increment for the next load
        currentSearchTerm.current = searchTerm; // Update ref for infinite scroll
      } catch (error) {
        const msg = handleError(error);
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [loading, requestConfig]
  );

  // Effect for initial load when menu opens
  useEffect(() => {
    if (open && !hasInitialLoad.current) {
      loadOptions(0, '', true); // Load initial data, mark as new search
      hasInitialLoad.current = true;
    }
  }, [open, loadOptions]);

  // Effect for handling debounced search changes
  useEffect(() => {
    // Only trigger if the menu is open and debouncedSearch has changed from the last successful search
    // And the debounced search is different from what we already loaded for the current page
    if (open && debouncedSearch !== currentSearchTerm.current) {
      loadOptions(0, debouncedSearch, true); // Load data for new search, mark as new search
    }
  }, [open, debouncedSearch, loadOptions]);

  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading,
    hasNextPage,
    // Pass the current `page` state and the `currentSearchTerm` from the ref
    onLoadMore: () => loadOptions(page, currentSearchTerm.current),
    disabled: Boolean(error || !open)
  });

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{ paper: { sx: { width: 300 } } }}
      >
        {/* Search Field */}
        <MenuItem disableRipple>
          <InputBase placeholder="Search..." value={search} onChange={(event) => setSearch(event.target.value)} />
        </MenuItem>

        <Box ref={rootRef} sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {options.map((option, index) => (
            <MenuItem key={`menu-option-${index}`} onClick={(event) => handleClick(event, option)}>
              {renderMenuItemContent ? renderMenuItemContent(option) : getOptionLabel(option)}
            </MenuItem>
          ))}

          {hasNextPage && (
            <MenuItem ref={infiniteRef} disabled>
              Loading...
            </MenuItem>
          )}

          {!hasNextPage && !loading && (
            <MenuItem disabled>{options.length > 0 ? '--- You have reached the end ---' : '--- No data to display ---'}</MenuItem>
          )}

          {error && (
            <MenuItem disabled style={{ color: 'red' }}>
              Error: {error}
            </MenuItem>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default PaginatedMenu;
